import client from './client';

export const getUserTypes = () =>
  client.get('/user-types');

export const getOnboardingStatus = () =>
  client.get('/onboarding/status');

export const setUserType = (userTypeId, subcategoryId = null) =>
  client.post('/onboarding/set-type', {
    user_type_id: userTypeId,
    subcategory_id: subcategoryId,
  });

export const getQuestions = () =>
  client.get('/onboarding/questions');

export const saveAnswers = (answers) =>
  client.post('/onboarding/answers', { answers });

/**
 * Whether a value is something the browser can actually upload.
 *
 * FormData stringifies anything that is not a File or Blob, so a stored path
 * standing in for an already-uploaded document would be sent as text and fail
 * the server's `file` rule (items 11, 14, 17).
 */
const isUploadable = (file) =>
  (typeof File !== 'undefined' && file instanceof File) ||
  (typeof Blob !== 'undefined' && file instanceof Blob);

/**
 * Save answers with file uploads using multipart/form-data.
 * @param {Array} answers - Array of { question_id, value } for non-file answers
 * @param {Object} fileAnswers - Map of questionId -> File[] for file-type answers
 * @param {Array} tableFileAnswers - Array of { questionId, rowIndex, columnKey, file } for files inside table cells
 * @param {Object} fileJustifications - Map of questionId -> justification text for
 *   documents that failed AI validation and the user chose to submit anyway
 */
export const saveAnswersWithFiles = (answers, fileAnswers, tableFileAnswers, fileJustifications) => {
  const formData = new FormData();

  // Append non-file answers
  answers.forEach((answer, index) => {
    formData.append(`answers[${index}][question_id]`, answer.question_id);
    const val = Array.isArray(answer.value) || (answer.value && typeof answer.value === 'object')
      ? JSON.stringify(answer.value)
      : (answer.value ?? '');
    formData.append(`answers[${index}][value]`, val);
    // The version we loaded, so the server can reject a save that would
    // overwrite a collaborator's newer answer (EOP-97).
    if (answer.version) {
      formData.append(`answers[${index}][version]`, answer.version);
    }
  });

  // Append file answers. Only a real File/Blob may be appended: FormData turns
  // anything else into a string, so an already-uploaded document -- held as a
  // stored path rather than a File -- arrived as text and was rejected by the
  // server's `required|file` rule with "The file_answers.N.file field is
  // required", even though every attachment was present. The same coercion
  // broke saving a requested change to a table that holds a file column, which
  // surfaced as a server error on the first attempt (items 11, 14, 17).
  let fileIndex = 0;
  Object.entries(fileAnswers || {}).forEach(([questionId, files]) => {
    (files || []).filter(isUploadable).forEach((file) => {
      formData.append(`file_answers[${fileIndex}][question_id]`, questionId);
      formData.append(`file_answers[${fileIndex}][file]`, file);
      fileIndex++;
    });
  });

  // Append per-cell files for table answers, re-indexed so the keys stay
  // sequential once already-uploaded cells are skipped.
  (tableFileAnswers || [])
    .filter((entry) => isUploadable(entry?.file))
    .forEach((entry, index) => {
      formData.append(`table_file_answers[${index}][question_id]`, entry.questionId);
      formData.append(`table_file_answers[${index}][row_index]`, entry.rowIndex);
      formData.append(`table_file_answers[${index}][column_key]`, entry.columnKey);
      formData.append(`table_file_answers[${index}][file]`, entry.file);
    });

  Object.entries(fileJustifications || {}).forEach(([questionId, text]) => {
    if (text) formData.append(`file_justifications[${questionId}]`, text);
  });

  return client.post('/onboarding/answers', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const completeStep = (stepId) =>
  client.post(`/onboarding/steps/${stepId}/complete`);

export const previousStep = (stepId) =>
  client.post(`/onboarding/steps/${stepId}/previous`);

// `returnToStepId` marks an out-of-order edit: once this step is completed the
// server sends the client straight back there (EOP-52).
export const gotoStep = (stepId, returnToStepId = null) =>
  client.post(
    `/onboarding/steps/${stepId}/goto`,
    returnToStepId ? { return_to: returnToStepId } : {}
  );

// Remove an already-uploaded document so it can be deleted or replaced (EOP-22).
export const deleteAnswerFile = (fileId) =>
  client.delete(`/onboarding/answers/files/${fileId}`);

export const reopenOnboarding = () =>
  client.post('/onboarding/reopen');

export const getTimeline = () =>
  client.get('/onboarding/timeline');

export const deleteDraft = () =>
  client.delete('/onboarding/draft');

// Fetches the PDF with the auth header and triggers a browser download.
export const downloadApplicationPdf = async () => {
  const response = await client.get('/onboarding/download-pdf', { responseType: 'blob' });

  // An error response still arrives as a blob because of responseType; read
  // it back so the caller gets the server's message instead of saving a
  // file full of JSON.
  if (response.data.type === 'application/json') {
    const text = await response.data.text();
    throw new Error(JSON.parse(text).message || 'Download failed.');
  }

  const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  // Needs Content-Disposition to be CORS-exposed (see config/cors.php).
  const disposition = response.headers['content-disposition'] || '';
  const match = disposition.match(/filename="?([^";]+)"?/);
  link.href = url;
  link.download = match ? match[1] : 'application.pdf';
  document.body.appendChild(link);
  link.click();

  // Revoking synchronously can abort an in-flight download in some
  // browsers — let the click settle first.
  setTimeout(() => {
    link.remove();
    URL.revokeObjectURL(url);
  }, 1000);
};

export const getRegistrationCatalog = () =>
  client.get('/onboarding/registration');

export const saveRegistration = (countryCode, values) =>
  client.post('/onboarding/registration', { country_code: countryCode, values });
